import { getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getSetLocalStorage } from '@/utils/tools';
import CryptoJS from 'crypto-js';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { reportSnapchatEvent } from '@/api/public';

// Snapchat Click ID 相关常量
const SC_CLICK_ID_LS_KEY = 'sc_click_id';
const SC_CLICK_ID_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 天有效期

interface SnapchatConversionsConfig {
  isEnabled?: boolean;
  platform: 'WEB' | 'MOBILE_APP';
  pageViewEventId?: string;
}

export class SnapchatConversionsProvider implements Provider {
  type = ProviderType.SNAPCHAT_CONVERSION;
  isEnabled: boolean;
  platform: 'WEB' | 'MOBILE_APP';
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private pageViewEventId?: string;

  constructor(config: SnapchatConversionsConfig) {
    this.isEnabled = config.isEnabled ?? true;
    this.platform = config.platform;
    this.pageViewEventId = config.pageViewEventId;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // 初始化时捕获 URL 中的 ScCid 参数并存储到 localStorage
      this.captureScClickId();

      this.initialized = true;
      // 替代 window.snaptr('track', 'PAGE_VIEW')：Conversions API 走接口上报初始页面浏览
      // 这里仅在 provider 首次初始化时触发一次，行为与 Pixel init 阶段的 PAGE_VIEW 对齐
      this.track({ eventName: 'PAGE_VIEW', eventId: this.pageViewEventId });
      return true;
    } catch (error) {
      logger.error('Failed to initialize SnapchatConversions Pixel:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      // 获取当前的 store 状态
      const storeState = getStoreState();

      const userInfo = storeState.userInfo;
      const { osVersion, deviceName } = this.getOSVersionAndDevice();
      const timeZoneAbbr = this.getTimezoneAbbreviation(); // 时区缩写

      // 先获取事件的原始参数（来自适配器）
      const params: any = {};
      const user_data: any = {};
      const custom_data = event.properties || {};
      const extinfo = [];
      // 故意这么写的，强调顺序的重要性, 并且能够与文档的数字一一对应。
      // https://developers.snap.com/api/marketing-api/Conversions-API/Parameters
      extinfo[0] = '1.0.0'; // Required extinfo version String, 目前产物上看不到版本号区分
      extinfo[1] = 'zeelool.com'; // App package name String
      extinfo[2] = '1.0.0'; // Short version String or int, 目前产物上看不到版本号区分
      extinfo[3] = '1.0.0'; // Long version long, 目前产物上看不到版本号区分
      extinfo[4] = osVersion; // Required OS version string
      extinfo[5] = deviceName; // Device model name String
      extinfo[6] = navigator.language || 'en-US'; // locale String
      extinfo[7] = timeZoneAbbr; // timezone abbreviation String
      extinfo[8] = ''; // carrier String (web 环境无法获取)
      extinfo[9] = screen.width || 0; // Screen width Int64
      extinfo[10] = screen.height || 0; // Screen height Int64
      extinfo[11] = String(window.devicePixelRatio || 1); // Screen density String
      extinfo[12] = navigator.hardwareConcurrency || 1; // CPU cores Int64
      extinfo[13] = ''; // External storage size in GB (web 环境无法获取)
      extinfo[14] = ''; // Free Space on external storage in GB (web 环境无法获取)
      extinfo[15] = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; // device timezone string

      const event_name = event.eventName;
      if (userInfo.email) {
        const email = userInfo.email.toLowerCase();
        const hash = CryptoJS.SHA256(email);
        user_data.em = [hash.toString(CryptoJS.enc.Hex)];
      }
      if (userInfo.telephone) {
        const telephone = `${userInfo.tel_code}${userInfo.telephone}`;
        const hash = CryptoJS.SHA256(telephone.replace(/\D/g, ''));
        user_data.ph = [hash.toString(CryptoJS.enc.Hex)];
      }
      user_data.client_user_agent = navigator.userAgent;
      // 每次上报前检查 URL 是否有新的 ScCid（用户可能从 Snapchat 广告跳转到新页面）
      this.captureScClickId();
      // 从 localStorage 读取 sc_click_id，添加到 user_data
      const scClickId = getSetLocalStorage.get(SC_CLICK_ID_LS_KEY);
      if (scClickId) {
        user_data.sc_click_id = scClickId;
      }
      params.event_time = Math.trunc(new Date().valueOf() / 1000);
      params.event_source_url = window.location.href;
      params.action_source = this.platform; // WEB / MOBILE_APP
      params.user_data = user_data;
      params.custom_data = custom_data;
      params.event_name = event_name;
      // 优先使用 event.eventId 作为统一的事件 ID
      if (event.eventId) {
        params.event_id = event.eventId;
      }
      params.app_data = {
        // 在纯 Web/H5 环境下无法获取 ATT（App Tracking Transparency）授权状态，因为 ATT 是苹果原生 iOS SDK 提供的功能，仅在原生 App 中可用
        // advertiser_tracking_enabled
        extinfo,
      };
      // 使用统一的日志控制方式
      if (this.loggingEnabled) {
        logger.log(`SnapchatConversions, Event: ${event.eventName}`, params);
      }
      reportSnapchatEvent({ data: [params] }).catch((err) => {
        logger.error('SnapchatConversions report event error:', err);
      });
    } catch (error) {
      logger.error('SnapchatConversions tracking error:', error);
    }
  }

  /**
   * 启用Snapchat日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Snapchat日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取操作系统版本和设备名称
   * @returns { osVersion: string, deviceName: string }
   */
  private getOSVersionAndDevice(): { osVersion: string; deviceName: string } {
    const ua = navigator.userAgent;
    let osVersion = '';
    let deviceName = '';

    // Windows
    const winMatch = ua.match(/Windows NT ([\d.]+)/);
    if (winMatch) {
      osVersion = winMatch[1]; // 例如 "10.0"
      deviceName = 'Windows PC';
      // 尝试获取更详细的 Windows 版本
      if (ua.includes('Win64')) deviceName = 'Windows 64-bit';
    }
    // macOS
    else if (/Mac OS X ([\d_]+)/.test(ua)) {
      osVersion = RegExp.$1.replace(/_/g, '.'); // 例如 "10.15.7"
      deviceName = 'Mac';
      // 尝试区分 MacBook / iMac 等（通常无法从 UA 准确获取）
      if (ua.includes('Macintosh')) deviceName = 'Macintosh';
    }
    // iOS (iPhone)
    else if (/iPhone.*OS ([\d_]+)/.test(ua)) {
      osVersion = RegExp.$1.replace(/_/g, '.'); // 例如 "15.0"
      // 尝试提取 iPhone 型号（通常 UA 中不包含具体型号）
      const iphoneMatch = ua.match(/iPhone(\d+[,_]\d+)?/);
      deviceName = iphoneMatch ? `iPhone${iphoneMatch[1] || ''}` : 'iPhone';
    }
    // iOS (iPad)
    else if (/iPad.*OS ([\d_]+)/.test(ua)) {
      osVersion = RegExp.$1.replace(/_/g, '.'); // 例如 "15.0"
      const ipadMatch = ua.match(/iPad(\d+[,_]\d+)?/);
      deviceName = ipadMatch ? `iPad${ipadMatch[1] || ''}` : 'iPad';
    }
    // Android
    else if (/Android ([\d.]+)/.test(ua)) {
      osVersion = RegExp.$1; // 例如 "12.0"
      // 尝试提取设备型号
      const buildMatch = ua.match(/;\s*([^;]+)\s+Build\//);
      deviceName = buildMatch ? buildMatch[1].trim() : 'Android Device';
    }
    // Linux (通用)
    else if (/Linux/.test(ua)) {
      osVersion = 'Linux';
      deviceName = 'Linux Device';
    }
    // Chrome OS
    else if (/CrOS/.test(ua)) {
      const crOsMatch = ua.match(/CrOS\s+[\w]+\s+([\d.]+)/);
      osVersion = crOsMatch ? crOsMatch[1] : 'Chrome OS';
      deviceName = 'Chromebook';
    }

    return {
      osVersion: osVersion || 'Unknown Version',
      deviceName: deviceName || 'Unknown Device',
    };
  }

  private getTimezoneAbbreviation(): string {
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find((p) => p.type === 'timeZoneName');
      return timeZonePart?.value || '';
    } catch {
      return '';
    }
  }

  /**
   * 从 URL 参数中捕获 ScCid（Snapchat Click ID）并存储到 localStorage
   * 有效期 7 天，参考 Snap Conversions API 文档: https://developers.snap.com/api/marketing-api/Conversions-API/Parameters
   */
  private captureScClickId(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const scCid = urlParams.get('ScCid');
      if (scCid) {
        getSetLocalStorage.set(SC_CLICK_ID_LS_KEY, scCid, Date.now() + SC_CLICK_ID_TTL_MS);
      }
    } catch (error) {
      logger.error('Failed to capture ScCid:', error);
    }
  }
}
